
import { AssessmentResult } from "../types";
import * as docx from "docx";
import * as XLSX from "xlsx";

declare const jspdf: any;
declare const JSZip: any;

const sanitizeFilename = (name: string): string => {
  return name.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
};

const ensureArray = (val: any): string[] => {
  if (Array.isArray(val)) return val.map(v => String(v));
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
};

const normalizeResult = (result: AssessmentResult): AssessmentResult => {
  return {
    ...result,
    criteriaResults: Array.isArray(result.criteriaResults) ? result.criteriaResults : [],
    generalStrengths: ensureArray(result.generalStrengths),
    areasForImprovement: ensureArray(result.areasForImprovement)
  };
};

export const generatePDFBlob = (rawResult: AssessmentResult): Blob => {
  const result = normalizeResult(rawResult);
  try {
    const jsPDFLib = (window as any).jspdf?.jsPDF;
    if (!jsPDFLib) {
      throw new Error("jsPDF library not initialized.");
    }

    const doc = new jsPDFLib();
    const pageHeight = doc.internal.pageSize.height || 297;
    const pageWidth = doc.internal.pageSize.width || 210;
    const margin = 20;
    
    // Header Branding
    doc.setFillColor(16, 34, 62); 
    doc.rect(margin, 12, 10, 15, 'F');
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(16, 34, 62);
    doc.text("University of Nottingham", margin + 15, 20);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("UK | CHINA | MALAYSIA", margin + 15, 25);

    // Score Badge
    const mark = result.overallMark ?? 0;
    const adjMark = result.adjustedMark ?? mark;
    const classification = (result.classification || "N/A").toUpperCase();
    
    doc.setFillColor(16, 34, 62); 
    doc.roundedRect(pageWidth - 85, 12, 65, 25, 3, 3, 'F');
    doc.setTextColor(255, 255, 255); 
    doc.setFontSize(16);
    doc.text(`Full: ${mark}%`, pageWidth - 52.5, 20, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Raw: ${result.totalEarned || 0} / ${result.totalMax || 0}`, pageWidth - 52.5, 25, { align: 'center' });
    doc.setFontSize(8);
    doc.text(`Adj (Excl. Visuals): ${adjMark}%`, pageWidth - 52.5, 30, { align: 'center' });
    doc.setFontSize(6);
    doc.text(classification, pageWidth - 52.5, 34, { align: 'center' });

    let currentY = 50;
    doc.setFontSize(20);
    doc.setTextColor(16, 34, 62);
    doc.text("Academic Feedback Report", margin, currentY);
    currentY += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139); 
    doc.text(`Student: ${result.studentName || 'N/A'} (${result.studentID || 'ID Hidden'})`, margin, currentY);
    currentY += 5;
    doc.text(`File: ${result.fileName || 'Unknown File'}`, margin, currentY);
    currentY += 5;
    doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, margin, currentY);

    currentY += 15;
    doc.setFontSize(14);
    doc.setTextColor(16, 34, 62);
    doc.text("Executive Summary", margin, currentY);
    currentY += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    const summary = result.summaryFeedback || "No summary feedback provided.";
    const splitSummary = doc.splitTextToSize(summary, pageWidth - (margin * 2));
    doc.text(splitSummary, margin, currentY);
    currentY += (splitSummary.length * 5) + 10;

    // Criteria Table
    if ((doc as any).autoTable) {
      const criteria = result.criteriaResults;
      (doc as any).autoTable({
        startY: currentY,
        head: [['Criterion', 'Mark', 'Status', 'Feedback']],
        body: criteria.map(c => [
          c.criterion || 'Criterion', 
          `${c.mark ?? 0}/${c.maxMark ?? 100}`, 
          c.isVisual ? 'Visual (N/A)' : 'Textual',
          c.feedback || 'No feedback.'
        ]),
        headStyles: { fillColor: [16, 34, 62], fontSize: 9 }, 
        columnStyles: {
          0: { cellWidth: 35, fontStyle: 'bold' },
          1: { cellWidth: 15, halign: 'center' },
          2: { cellWidth: 20, halign: 'center' },
          3: { cellWidth: 'auto' }
        },
        styles: { fontSize: 8, cellPadding: 4 },
        margin: { left: margin, right: margin }
      });
      currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    const ensureSpace = (needed: number) => {
      if (currentY + needed > pageHeight - 20) {
        doc.addPage();
        currentY = 25;
        return true;
      }
      return false;
    };

    // Strengths
    const strengths = result.generalStrengths;
    if (strengths.length > 0) {
      ensureSpace(30);
      doc.setFontSize(12);
      doc.setTextColor(169, 204, 23); 
      doc.text("Key Strengths", margin, currentY);
      currentY += 7;
      
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      strengths.forEach(s => {
        const lines = doc.splitTextToSize(`• ${s}`, pageWidth - (margin * 2 + 5));
        ensureSpace(lines.length * 5 + 2);
        doc.text(lines, margin + 5, currentY);
        currentY += (lines.length * 5);
      });
      currentY += 10;
    }

    // Improvements
    const improvements = result.areasForImprovement;
    if (improvements.length > 0) {
      ensureSpace(30);
      doc.setFontSize(12);
      doc.setTextColor(208, 1, 59); 
      doc.text("Areas for Development", margin, currentY);
      currentY += 7;

      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      improvements.forEach(a => {
        const lines = doc.splitTextToSize(`• ${a}`, pageWidth - (margin * 2 + 5));
        ensureSpace(lines.length * 5 + 2);
        doc.text(lines, margin + 5, currentY);
        currentY += (lines.length * 5);
      });
    }

    // Footer
    currentY = pageHeight - 15;
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text("Note: Adjusted mark (Adj) removes figures/diagrams for pro-rata calculation. Final marks subject to UoN moderation.", pageWidth / 2, currentY, { align: 'center' });

    return doc.output('blob');
  } catch (error) {
    console.error("Critical error during PDF generation:", error);
    throw error;
  }
};

export const downloadAsPDF = (result: AssessmentResult) => {
  try {
    const blob = generatePDFBlob(result);
    const title = result.studentName || result.fileName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sanitizeFilename(title)}_Feedback.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    alert("Report generation failed.");
  }
};

export const generateWordBlob = async (rawResult: AssessmentResult): Promise<Blob> => {
  const result = normalizeResult(rawResult);
  const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, HeadingLevel, AlignmentType, BorderStyle } = docx;

  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({
          children: [
            new TextRun({ text: "University of Nottingham", bold: true, size: 28, color: "10223e" }),
          ],
          spacing: { after: 400 }
        }),
        new Paragraph({
          children: [new TextRun({ text: "Academic Feedback Report", bold: true, size: 36, color: "10223e" })],
          spacing: { after: 400 }
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Student: ${result.studentName || 'N/A'} (${result.studentID || 'ID Hidden'})`, bold: true }),
            new TextRun({ text: `\nFile: ${result.fileName}`, break: 1 }),
            new TextRun({ text: `\nDate: ${new Date().toLocaleDateString('en-GB')}`, break: 1 }),
          ],
          border: { bottom: { color: "c8d7e1", space: 10, style: BorderStyle.SINGLE, size: 6 } },
          spacing: { after: 400 }
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Overall Draft Mark: ${result.overallMark}%`, bold: true, size: 28, color: "00549b" }),
            new TextRun({ text: `\nRaw Mark: ${result.totalEarned || 0} / ${result.totalMax || 0}`, break: 1, bold: true, size: 20, color: "475569" }),
            new TextRun({ text: `\nAdjusted Mark (Excl. Visuals): ${result.adjustedMark}%`, break: 1, bold: true, size: 24, color: "10223e" }),
            new TextRun({ text: `\nClassification: ${result.classification}`, break: 1, italics: true }),
          ],
          spacing: { after: 600 }
        }),
        new Paragraph({
          children: [new TextRun({ text: "Executive Summary", bold: true, size: 24 })],
          heading: HeadingLevel.HEADING_2,
          spacing: { after: 200 }
        }),
        new Paragraph({
          children: [new TextRun({ text: result.summaryFeedback, italics: true })],
          spacing: { after: 400 }
        }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Criterion", bold: true })] })], shading: { fill: "f1f5f9" } }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Mark", bold: true })] })], shading: { fill: "f1f5f9" } }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true })] })], shading: { fill: "f1f5f9" } }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Feedback", bold: true })] })], shading: { fill: "f1f5f9" } }),
              ]
            }),
            ...result.criteriaResults.map(c => new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ text: c.criterion })] }),
                new TableCell({ children: [new Paragraph({ text: `${c.mark}/${c.maxMark}` })] }),
                new TableCell({ children: [new Paragraph({ text: c.isVisual ? "Visual (N/A)" : "Textual" })] }),
                new TableCell({ children: [new Paragraph({ text: c.feedback })] }),
              ]
            }))
          ]
        }),
        new Paragraph({
          children: [new TextRun({ text: "Strengths", bold: true, size: 24 })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        ...result.generalStrengths.map(s => new Paragraph({ text: s, bullet: { level: 0 } })),
        new Paragraph({
          children: [new TextRun({ text: "Areas for Development", bold: true, size: 24 })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        ...result.areasForImprovement.map(a => new Paragraph({ text: a, bullet: { level: 0 } })),
        new Paragraph({
          children: [new TextRun({ text: "\n\nNote: This is an AI-assisted draft. Figures/Diagrams marked N/A were excluded from adjusted calculation.", size: 14, color: "999999" })],
          alignment: AlignmentType.CENTER,
          spacing: { before: 800 }
        })
      ],
    }],
  });

  return await Packer.toBlob(doc);
};

export const downloadAsWord = async (result: AssessmentResult) => {
  try {
    const blob = await generateWordBlob(result);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sanitizeFilename(result.studentName || result.fileName)}_Feedback.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    alert("Could not create Word document.");
  }
};

export const downloadBatchAsExcel = (results: AssessmentResult[]) => {
  const data = results.map(raw => {
    const r = normalizeResult(raw);
    const spread = (r.maxMark || r.overallMark) - (r.minMark || r.overallMark);
    const confidence = spread <= 3 ? "High" : spread <= 8 ? "Moderate" : "Low";
    
    return {
      "Student Name": r.studentName || "N/A",
      "Student ID": r.studentID || "N/A",
      "File Name": r.fileName,
      "Raw Mark": r.totalEarned || 0,
      "Total Possible": r.totalMax || 0,
      "Draft Mark (%)": r.overallMark,
      "Adjusted Mark (Excl. Visuals) (%)": r.adjustedMark || r.overallMark,
      "Min Likely Mark (%)": r.minMark || r.overallMark,
      "Max Likely Mark (%)": r.maxMark || r.overallMark,
      "AI Confidence": confidence,
      "Classification": r.classification,
      "Summary Feedback": r.summaryFeedback,
      "Strengths": r.generalStrengths.join("; "),
      "Improvements": r.areasForImprovement.join("; ")
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Marks and Feedback");
  XLSX.writeFile(workbook, `PharmGrade_Batch_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
};

export const downloadBatchReportsAsZip = async (results: AssessmentResult[], format: 'pdf' | 'docx') => {
  try {
    const zip = new JSZip();
    const folderName = `PharmGrade_Reports_${format.toUpperCase()}`;
    const folder = zip.folder(folderName);

    if (!folder) return;

    for (let i = 0; i < results.length; i++) {
      const result = normalizeResult(results[i]);
      const baseName = sanitizeFilename(result.studentName || result.fileName || `Submission_${i + 1}`);
      const filename = `${i + 1}_${baseName}_Report.${format}`;
      
      try {
        if (format === 'pdf') {
          const blob = generatePDFBlob(result);
          folder.file(filename, blob);
        } else {
          const blob = await generateWordBlob(result);
          folder.file(filename, blob);
        }
      } catch (e) {
        console.error(`Skipping batch item ${i + 1} due to error: ${filename}`, e);
      }
    }

    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PharmGrade_Feedback_Batch_${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    alert("Could not generate zip archive.");
  }
};
